import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Eye, CheckCircle, XCircle, AlertCircle, Clock, ChevronLeft, ChevronRight,
  MapPin, Star, Users, Ban, RotateCcw,
} from 'lucide-react';
import apiService from '../../apiService';
import { useLiveRefresh } from '../../liveRefresh';
import Img from '../../components/Img';
import CityFilter from '../../components/CityFilter';

// Salles & espaces événementiels — validation et suivi (service « validation »).
// API : GET /venues/admin/list, PATCH /venues/admin/:id/moderate, PATCH /venues/admin/:id/suspend

const STATUS = {
  pending_review: { label: 'À valider', bg: '#fef9c3', color: '#ca8a04', icon: Clock },
  approved:       { label: 'Validée',   bg: '#ECFDF5', color: '#10B981', icon: CheckCircle },
  rejected:       { label: 'Refusée',   bg: '#fecaca', color: '#dc2626', icon: XCircle },
  draft:          { label: 'Brouillon', bg: '#F4F5F7', color: '#5C636E', icon: AlertCircle },
};
const TYPE_LABELS = {
  party_hall: '🎉 Salle de fête', show_hall: '🎭 Salle de spectacle', conference: '🎤 Salle de conférence',
  outdoor: '🌳 Espace extérieur', tent: '⛺ Chapiteau', rooftop: '🏙️ Rooftop', other: '🏛️ Autre',
};
const FORMULA_LABELS = { full_day: 'Journée', morning: 'Matin', afternoon: 'Après-midi', evening: 'Soirée', night: 'Nuit', weekend: 'Week-end' };
const AMENITY_LABELS = {
  stage: 'Scène', sound_system: 'Sono', projector: 'Projecteur / écran', air_conditioning: 'Climatisation', generator: 'Groupe électrogène',
  parking: 'Parking', kitchen: 'Cuisine', toilets: 'Toilettes', dressing_room: 'Loge', wifi: 'Wi-Fi', wheelchair: 'Accès PMR',
  security: 'Sécurité', lighting: 'Éclairage', tables_chairs: 'Tables & chaises', bar: 'Bar', garden: 'Jardin',
};
const CDF = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} CDF`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function Badge({ status, suspended }) {
  if (suspended) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fecaca', color: '#dc2626', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}><Ban size={12} /> Suspendue</span>;
  const cfg = STATUS[status] || STATUS.draft; const Icon = cfg.icon;
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: cfg.bg, color: cfg.color, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}><Icon size={12} /> {cfg.label}</span>;
}

function InfoBox({ label, value }) {
  return (
    <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '8px 12px' }}>
      <div style={{ fontSize: 11, color: '#9AA0A8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#12151A', marginTop: 2 }}>{value || '—'}</div>
    </div>
  );
}

function VenueModal({ venue, onClose, onModerate, onSuspend }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  if (!venue) return null;
  const v = venue;
  const run = async (fn) => { setBusy(true); try { await fn(); onClose(); } finally { setBusy(false); } };
  const cap = `${v.capacity_seated || 0} assis · ${v.capacity_standing || 0} debout`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 760, maxHeight: '92vh', overflow: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{v.name}</h2>
            <p style={{ margin: '4px 0 0', color: '#5C636E', fontSize: 14 }}>
              {TYPE_LABELS[v.venue_type] || v.venue_type} · {v.city_slug} · {v.supplier?.business_name || v.supplier_id}{v.supplier?.phone ? ` · ${v.supplier.phone}` : ''}
            </p>
            <div style={{ marginTop: 8 }}><Badge status={v.status} suspended={v.suspended} /></div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5C636E', fontSize: 22 }}>✕</button>
        </div>

        {(v.rejection_reason || v.suspension_reason || v.moderation_note) && (
          <p style={{ margin: '0 0 12px', color: '#DC2626', fontSize: 13, padding: '8px 12px', background: '#fef2f2', borderRadius: 8 }}>
            {v.suspended ? `Suspension : ${v.suspension_reason || '—'}` : v.status === 'rejected' ? `Refus : ${v.rejection_reason}` : `Note : ${v.moderation_note}`}
          </p>
        )}

        {v.cover_image && <Img src={v.cover_image} alt="" style={{ width: '100%', height: 220, objectFit: 'cover', borderRadius: 12, marginBottom: 10 }} />}
        {v.images?.length > 0 && (
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 16 }}>
            {v.images.map(im => <Img key={im.image_id} src={im.url} thumb={im.thumb} alt="" style={{ width: 110, height: 80, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />)}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <InfoBox label="Adresse" value={v.location?.address} />
          <InfoBox label="Position" value={v.location?.lat != null ? `${v.location.lat.toFixed(5)}, ${v.location.lng.toFixed(5)}` : 'non placée'} />
          <InfoBox label="Capacité" value={cap} />
          <InfoBox label="Surface" value={v.surface_m2 ? `${v.surface_m2} m²` : '—'} />
          <InfoBox label="Acompte" value={`${v.deposit_percent} %`} />
          <InfoBox label="Caution" value={v.security_deposit?.required ? CDF(v.security_deposit.amount) : 'Aucune'} />
          <InfoBox label="Solde restant" value={v.all_payments_via_mbiyo ? 'Via Mbiyo' : 'Au propriétaire'} />
          <InfoBox label="Tampon / délai" value={`${v.buffer_minutes} min · ${v.min_notice_hours} h · ${v.max_advance_days} j`} />
          <InfoBox label="Note" value={v.rating_count ? `${Number(v.rating).toFixed(1)} (${v.rating_count})` : 'Nouveau'} />
          <InfoBox label="Réservations / annulations propriétaire" value={`${v.bookings_count || 0} / ${v.supplier_cancellations || 0}`} />
        </div>

        {v.description && <p style={{ margin: '0 0 14px', fontSize: 13, color: '#414852', lineHeight: 1.6, background: '#F9FAFB', padding: 12, borderRadius: 8 }}>{v.description}</p>}

        <div style={{ marginBottom: 14 }}>
          <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600 }}>Formules</p>
          {(v.formulas || []).filter(f => f.enabled !== false).length === 0 && <span style={{ fontSize: 13, color: '#9AA0A8' }}>Aucune formule</span>}
          {(v.formulas || []).filter(f => f.enabled !== false).map(f => (
            <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 10px', background: '#F9FAFB', borderRadius: 8, marginBottom: 4 }}>
              <span>{f.label || FORMULA_LABELS[f.key]} · {f.start_time} → {f.end_time}</span>
              <span style={{ fontWeight: 600 }}>{CDF(f.price_weekday)}{f.price_weekend ? ` · WE ${CDF(f.price_weekend)}` : ''}</span>
            </div>
          ))}
          {v.hourly?.enabled && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 10px', background: '#F9FAFB', borderRadius: 8 }}>
              <span>À l'heure · min. {v.hourly.min_hours} h · {v.hourly.open_time} → {v.hourly.close_time}</span>
              <span style={{ fontWeight: 600 }}>{CDF(v.hourly.price_per_hour)}/h{v.hourly.price_per_hour_weekend ? ` · WE ${CDF(v.hourly.price_per_hour_weekend)}/h` : ''}</span>
            </div>
          )}
        </div>

        {v.options?.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600 }}>Options payantes</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {v.options.map(o => <span key={o.option_id} style={{ background: '#FEE9E2', color: '#C9350F', padding: '3px 10px', borderRadius: 10, fontSize: 12 }}>{o.label} · {CDF(o.price)}{o.pricing === 'per_guest' ? '/invité' : o.pricing === 'per_hour' ? '/h' : ''}</span>)}
            </div>
          </div>
        )}

        {v.amenities?.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600 }}>Équipements</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {v.amenities.map(a => <span key={a} style={{ background: '#F4F5F7', color: '#414852', padding: '3px 10px', borderRadius: 10, fontSize: 12 }}>{AMENITY_LABELS[a] || a}</span>)}
            </div>
          </div>
        )}

        {v.rules && (
          <div style={{ marginBottom: 14, fontSize: 13, color: '#414852' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>Règles</p>
            Heure limite : {v.rules.curfew_time || '—'} · Alcool : {v.rules.alcohol_allowed ? 'oui' : 'non'} · Traiteur : {{ free: 'libre', imposed: 'imposé', forbidden: 'interdit' }[v.rules.external_catering] || '—'} · Décoration : {v.rules.decoration_allowed ? 'oui' : 'non'}
            {v.rules.notes ? <div style={{ marginTop: 4 }}>{v.rules.notes}</div> : null}
          </div>
        )}

        <div style={{ borderTop: '1px solid #F4F5F7', paddingTop: 14 }}>
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Note / motif (obligatoire pour un refus ou une suspension)" rows={2}
            style={{ width: '100%', padding: 10, border: '1px solid #DCE0E6', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 10 }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {v.status !== 'approved' && (
              <button disabled={busy} onClick={() => run(() => onModerate(v.venue_id, 'approved', note))} style={btn('#10B981')}><CheckCircle size={14} /> Valider</button>
            )}
            {v.status !== 'rejected' && (
              <button disabled={busy || !note.trim()} onClick={() => run(() => onModerate(v.venue_id, 'rejected', note))} style={btn('#dc2626', !note.trim())}><XCircle size={14} /> Refuser</button>
            )}
            {v.status === 'approved' && (v.suspended
              ? <button disabled={busy} onClick={() => run(() => onSuspend(v.venue_id, false, ''))} style={btn('#5C636E')}><RotateCcw size={14} /> Rétablir</button>
              : <button disabled={busy || !note.trim()} onClick={() => run(() => onSuspend(v.venue_id, true, note))} style={btn('#ea580c', !note.trim())}><Ban size={14} /> Suspendre</button>)}
          </div>
        </div>
      </div>
    </div>
  );
}

const btn = (bg, disabled = false) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color: '#fff', border: 'none', borderRadius: 8,
  padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
});

export default function VenuesAdminPage() {
  const [venues, setVenues] = useState([]);
  const [status, setStatus] = useState('pending_review');
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState(''); // '' = toutes les villes du périmètre
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page, limit: 25, ...(status ? { status } : {}), ...(search ? { search } : {}), ...(cityFilter ? { city_slug: cityFilter } : {}) });
      const res = await apiService.get(`/venues/admin/list?${qs}`, { userType: 'admin' });
      setVenues(res.venues || []); setPages(res.pages || 1); setTotal(res.total || 0); setPending(res.pending || 0);
    } catch (e) { setToast(e.message || 'Erreur de chargement'); }
    finally { setLoading(false); }
  }, [page, status, search, cityFilter]);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(), ['venue_pending']);

  const flash = (t) => { setToast(t); setTimeout(() => setToast(''), 3000); };
  const moderate = async (id, st, note) => {
    try { await apiService.patch(`/venues/admin/${id}/moderate`, { status: st, note }, { userType: 'admin' }); flash(st === 'approved' ? 'Salle validée ✅' : 'Salle refusée'); load(); }
    catch (e) { flash(e.message || 'Erreur'); throw e; }
  };
  const suspend = async (id, suspended, reason) => {
    try { await apiService.patch(`/venues/admin/${id}/suspend`, { suspended, reason }, { userType: 'admin' }); flash(suspended ? 'Salle suspendue' : 'Salle rétablie'); load(); }
    catch (e) { flash(e.message || 'Erreur'); throw e; }
  };

  const tabs = [['pending_review', `À valider${pending ? ` (${pending})` : ''}`], ['approved', 'Validées'], ['rejected', 'Refusées'], ['', 'Toutes']];

  return (
    <div className="dashboard-content">
      <h1 className="page-title">🎉 Salles & espaces événementiels</h1>
      <p style={{ margin: '0 0 1rem', color: '#5C636E', fontSize: 14 }}>Chaque salle est validée avant d'apparaître dans l'app client. Les photos, l'adresse et la capacité repassent en validation à chaque modification.</p>
      {toast && <div style={{ padding: '10px 14px', background: '#FEE9E2', color: '#C9350F', borderRadius: 10, marginBottom: 12, fontWeight: 600 }}>{toast}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => { setStatus(k); setPage(1); }} style={{ padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: status === k ? '#F54119' : '#F4F5F7', color: status === k ? '#fff' : '#414852' }}>{l}</button>
        ))}
        <CityFilter value={cityFilter} onChange={(v) => { setCityFilter(v); setPage(1); }} className="" style={{ marginLeft: 'auto', background: '#fff', border: '1px solid #DCE0E6', borderRadius: 10, padding: '7px 10px', fontSize: 13, cursor: 'pointer', outline: 'none' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #DCE0E6', borderRadius: 10, padding: '6px 10px' }}>
          <Search size={14} color="#9AA0A8" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Nom de la salle" style={{ border: 'none', outline: 'none', fontSize: 13, width: 180 }} />
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB', textAlign: 'left', fontSize: 12, color: '#5C636E', textTransform: 'uppercase' }}>
              {['Salle', 'Commerce', 'Capacité', 'À partir de', 'Statut', 'Créée', ''].map(h => <th key={h} style={{ padding: '10px 16px', fontWeight: 600 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 48, color: '#9AA0A8' }}>Chargement...</td></tr>
              : venues.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 48, color: '#9AA0A8' }}>Aucune salle</td></tr>
              : venues.map(v => (
                <tr key={v.venue_id} style={{ borderBottom: '1px solid #F4F5F7' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {v.cover_image ? <Img src={v.cover_image} thumb={v.cover_image_thumb} alt="" style={{ width: 52, height: 40, objectFit: 'cover', borderRadius: 6 }} /> : <div style={{ width: 52, height: 40, borderRadius: 6, background: '#F4F5F7' }} />}
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{v.name}</div>
                        <div style={{ fontSize: 12, color: '#9AA0A8' }}>{TYPE_LABELS[v.venue_type] || v.venue_type}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13 }}>
                    <div style={{ fontWeight: 600 }}>{v.supplier?.business_name || v.supplier_id}</div>
                    <div style={{ fontSize: 12, color: '#5C636E', display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={11} /> {v.city_slug}</div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13 }}><Users size={13} style={{ verticalAlign: -2 }} /> {v.capacity_seated || 0} / {v.capacity_standing || 0}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600 }}>{v.min_price ? CDF(v.min_price) : '—'}</td>
                  <td style={{ padding: '12px 16px' }}><Badge status={v.status} suspended={v.suspended} /></td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#5C636E' }}>
                    {fmtDate(v.created_at)}
                    {v.rating_count > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, color: '#FFB900' }}><Star size={11} fill="#FFB900" /> {Number(v.rating).toFixed(1)} ({v.rating_count})</div>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <button onClick={() => setView(v)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#FEE9E2', color: '#C9350F', border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}><Eye size={14} /> Examiner</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ background: '#F4F5F7', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}><ChevronLeft size={16} /></button>
          <span style={{ fontSize: 14 }}>Page {page} / {pages} ({total})</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} style={{ background: '#F4F5F7', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}><ChevronRight size={16} /></button>
        </div>
      )}

      {view && <VenueModal venue={view} onClose={() => setView(null)} onModerate={moderate} onSuspend={suspend} />}
    </div>
  );
}
